import {
  Box,
  Checkbox,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  withStyles,
} from '@material-ui/core';
import React from 'react';

import { IListViewTableColumnInfo, Order } from '~/src/dash/components/tables/ListViewTable/common';
import { COLUMN_HEADER_STYLE } from '~/src/dash/components/tables/ListViewTable/common';
import { publicaColors } from '~/src/dash/utils/colors';

const disableSortStyle = {
  cursor: 'default',
  color: 'inherit',
};

const StyledTableSortLabel = withStyles(() => ({
  root: { verticalAlign: 'text-bottom' },
}))(TableSortLabel);

interface IListViewTableHeadProps {
  backgroundColor: string;
  className: string;
  enableActions?: boolean;
  enableStatusSwitch?: boolean;
  enableSelectCheckbox?: boolean;
  numSelected: number;
  onClickColumnHeader?: (column: IListViewTableColumnInfo) => void;
  onSelectAllClick?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  order: Order;
  orderBy: string;
  visibleColumns: IListViewTableColumnInfo[];
  visibleRowCount: number;
}

export const ListViewTableHead = ({
  backgroundColor,
  className,
  enableActions,
  enableStatusSwitch,
  enableSelectCheckbox,
  numSelected,
  onClickColumnHeader,
  onSelectAllClick = () => null,
  order,
  orderBy,
  visibleColumns,
  visibleRowCount,
}: IListViewTableHeadProps) => {
  return (
    <TableHead>
      <TableRow>
        {/* SELECT CHECKBOX */}
        {enableSelectCheckbox && (
          <TableCell
            padding='checkbox'
            style={{
              paddingRight: '12px',
              verticalAlign: 'text-bottom',
            }}
          >
            <Checkbox
              color='primary'
              indeterminate={numSelected > 0 && numSelected < visibleRowCount}
              checked={visibleRowCount > 0 && numSelected === visibleRowCount}
              onChange={onSelectAllClick}
              inputProps={{
                'aria-label': 'Select all items.',
              }}
              className={`cypress-${className}-tablecheckbox-selectall`}
              style={{
                transform: 'scale(0.9)',
                paddingBottom: '12px',
              }}
            />
          </TableCell>
        )}
        {visibleColumns.map((column: IListViewTableColumnInfo) => {
          const {
            defaultOrderDirection,
            disableSort,
            headerJSX,
            headerRenderType,
            headerStyle,
            headerTooltipText = '',
            isHidden,
            key,
            label,
            orderByKey,
            requestKey,
          } = column;

          const isOrderByUnchanged = orderBy === (orderByKey || requestKey || key);

          // conditional checks to see if we should hide the column
          if (
            isHidden ||
            (!enableStatusSwitch && key === 'Status') ||
            (!enableActions && key === 'Actions')
          ) {
            return null;
          }

          return (
            <TableCell
              key={key}
              padding='none'
              sortDirection={isOrderByUnchanged ? order : defaultOrderDirection || false}
              style={{
                position: 'relative',
                background: backgroundColor,
                borderBottom: `1px solid ${publicaColors.lightGray}`,
                paddingBottom: '2px',
                minWidth: '192px',
                textAlign: 'left',
                verticalAlign: 'text-bottom',
                ...(headerRenderType ? COLUMN_HEADER_STYLE[headerRenderType] : {}),
                ...(headerStyle || {}),
              }}
            >
              <Tooltip title={headerTooltipText} placement='top'>
                <StyledTableSortLabel
                  active={isOrderByUnchanged}
                  direction={isOrderByUnchanged ? order : defaultOrderDirection || 'asc'}
                  hideSortIcon={disableSort}
                  onClick={() => {
                    if (disableSort) {
                      return;
                    }
                    if (onClickColumnHeader) {
                      onClickColumnHeader(column);
                    }
                  }}
                  style={disableSort ? { ...disableSortStyle } : {}}
                >
                  {headerJSX ? headerJSX({ label, column }) : label}
                  {isOrderByUnchanged && (
                    <Box component='span' style={{ display: 'none' }}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  )}
                </StyledTableSortLabel>
              </Tooltip>
            </TableCell>
          );
        })}
      </TableRow>
    </TableHead>
  );
};
